/** Shared time-parsing/formatting helpers.
 *
 *  Extracted from TodayTab.tsx so that SchedulePreview (and anything else
 *  that needs to convert a display time to 24-hour storage format) can
 *  import them without a static dependency on the (lazy-loaded) TodayTab
 *  chunk. A static import from TodayTab pulls its entire module into the
 *  importer's bundle, defeating the route-level code split (Issue #79).
 *  TodayTab imports from here too — same implementation, one module.
 */

/** Parse "H:MM AM/PM" to minutes since midnight; unparseable → very large.
 *  Tolerates: "3:00 PM", "3:00PM", "3pm", "3 PM", "3:00 pm", "15:00".
 *  Returns 99999 for null/undefined/"TBD"/unparseable so callers treat the
 *  event as having no time. */
export function parseTimeToMinutes(t?: string | null): number {
  if (!t) return 99999;
  const raw = String(t).trim();
  if (!raw) return 99999;
  if (/^tbd$/i.test(raw)) return 99999;

  // 12-hour format with optional minutes and optional space before AM/PM.
  const ampm = raw.match(/^(\d{1,2})(?::(\d{2}))?\s*([AP])\.?\s*M\.?$/i);
  if (ampm) {
    let h = parseInt(ampm[1], 10);
    const min = ampm[2] ? parseInt(ampm[2], 10) : 0;
    const isPm = ampm[3].toUpperCase() === "P";
    if (Number.isNaN(h) || Number.isNaN(min) || h < 0 || h > 12 || min < 0 || min > 59) return 99999;
    if (isPm && h !== 12) h += 12;
    if (!isPm && h === 12) h = 0;
    return h * 60 + min;
  }

  // 24-hour format "15:00".
  const h24 = raw.match(/^(\d{1,2}):(\d{2})$/);
  if (h24) {
    const h = parseInt(h24[1], 10);
    const min = parseInt(h24[2], 10);
    if (Number.isNaN(h) || Number.isNaN(min) || h < 0 || h > 23 || min < 0 || min > 59) return 99999;
    return h * 60 + min;
  }

  return 99999;
}

/** Converts any display time string to 24-hour "HH:MM:00" for events-table
 *  storage — matches the format RecurrenceChip and the reschedule path
 *  already write (Issue #43: persistSchedule was writing "H:MM AM/PM"
 *  instead, which every reader's `.slice(0,5)` 24-hour assumption mangled).
 *  Returns null when the input isn't a real clock time (TBD, all-day, etc). */
export function displayTimeTo24Hour(display?: string | null): string | null {
  const mins = parseTimeToMinutes(display);
  if (mins >= 99999) return null;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:00`;
}

/** The fallback a row with no usable time reads as. Not blank (which looks
 *  like a rendering bug) and not "Invalid Date" — she set no time, so the
 *  honest answer is that it can arrive whenever. */
export const NO_TIME_LABEL = "Anytime";

/** "08:00:00" or "08:00" → "8:00 AM". Anything unusable → "Anytime".
 *
 *  #479 — this used to be `t.split(":")` with no guard, in TasksTab.tsx, on a
 *  render path every scheduled-task row goes through. `scheduled_tasks
 *  .schedule_time` is nullable (`string | null` in the generated types), and
 *  four insert paths omit the column entirely and lean on the DB default. A
 *  fixture row without one took down the whole Tasks tab: `null.split` throws
 *  during render, and React unmounts the subtree rather than skipping the row.
 *
 *  It also renders the value she is about to be shown in a success toast, so
 *  a cleared `<input type="time">` ("") produced the literal string
 *  "NaN:undefined AM" in her receipt.
 *
 *  Lives here rather than in TasksTab so it can be tested without importing a
 *  15k-line component — same reason parseTimeToMinutes moved out of TodayTab.
 */
export function formatTime12h(t?: string | null): string {
  if (typeof t !== "string") return NO_TIME_LABEL;
  const m = /^(\d{1,2}):(\d{2})(?::(\d{2}))?$/.exec(t.trim());
  if (!m) return NO_TIME_LABEL;
  const h = parseInt(m[1], 10);
  const min = parseInt(m[2], 10);
  // "24:00" and "08:99" parse as numbers but are not times. Guard the ranges
  // rather than letting them through as "12:99 AM".
  if (h < 0 || h > 23 || min < 0 || min > 59) return NO_TIME_LABEL;
  const ampm = h >= 12 ? "PM" : "AM";
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${m[2]} ${ampm}`;
}

/** True when a schedule_time is actually usable — i.e. formatTime12h will
 *  return a real clock time rather than the fallback. Callers that build a
 *  sentence around the time ("Every Monday at 8:00 AM") use this to drop the
 *  clause instead of emitting "Every Monday at Anytime". */
export function hasUsableTime(t?: string | null): boolean {
  return formatTime12h(t) !== NO_TIME_LABEL;
}
